import React, { forwardRef, useEffect, useLayoutEffect, useRef, useState, useCallback, useImperativeHandle } from 'react';
import Quill from 'quill';
import 'quill/dist/quill.snow.css';
import ImageModal from './ImageModal';

// Import Quill built-in modules
import Image from 'quill/formats/image';
import Video from 'quill/formats/video';
import Link from 'quill/formats/link';

// Register formats
Quill.register(Image, true);
Quill.register(Video, true);
Quill.register(Link, true);

// Editor is an uncontrolled React component
const Editor = forwardRef(
  ({ readOnly, defaultValue, onTextChange, onSelectionChange, userid }, ref) => {
    const containerRef = useRef(null);
    const defaultValueRef = useRef(defaultValue);
    const onTextChangeRef = useRef(onTextChange);
    const onSelectionChangeRef = useRef(onSelectionChange);
    const quillInstanceRef = useRef(null);
    const [isImageModalOpen, setIsImageModalOpen] = useState(false);
    const initializedRef = useRef(false);

    // 暴露方法给父组件
    useImperativeHandle(ref, () => ({
      enable: (enabled) => {
        if (quillInstanceRef.current) {
          quillInstanceRef.current.enable(enabled);
        }
      },
      setContents: (content) => {
        if (quillInstanceRef.current && !initializedRef.current) {
          if (typeof content === 'string') {
            quillInstanceRef.current.root.innerHTML = content;
          } else {
            quillInstanceRef.current.setContents(content);
          }
          initializedRef.current = true;
        }
      },
      getContents: () => {
        return quillInstanceRef.current ? quillInstanceRef.current.getContents() : null;
      },
      getHTML: () => {
        return quillInstanceRef.current ? quillInstanceRef.current.root.innerHTML : '';
      }
    }));

    const handleImageSelect = (imageUrl) => {
      imageUrl = imageUrl.replace(/(\.(png|jpg|jpeg)).*$/i, '$1');
      if (quillInstanceRef.current) {
        let range = quillInstanceRef.current.getSelection();
        
        // 如果没有获取到光标位置，尝试聚焦编辑器并重新获取
        if (!range) {
          quillInstanceRef.current.focus();
          range = quillInstanceRef.current.getSelection();
          
          // 如果仍然没有获取到光标位置，获取编辑器内容的长度并插入到末尾
          if (!range) {
            const length = quillInstanceRef.current.getLength();
            quillInstanceRef.current.insertEmbed(length - 1, 'image', imageUrl);
          } else {
            quillInstanceRef.current.insertEmbed(range.index, 'image', imageUrl);
          }
        } else {
          quillInstanceRef.current.insertEmbed(range.index, 'image', imageUrl);
        }
        
        // 手动触发TEXT_CHANGE事件以更新编辑器内容
        setTimeout(() => {
          if (quillInstanceRef.current) {
            const delta = quillInstanceRef.current.getContents();
            const oldDelta = { ops: [] };
            quillInstanceRef.current.emitter.emit(Quill.events.TEXT_CHANGE, delta, oldDelta, 'user');
          }
        }, 0);
      }
      setIsImageModalOpen(false);
    };

    const handleImageModalClose = () => {
      setIsImageModalOpen(false);
    };

    useLayoutEffect(() => {
      onTextChangeRef.current = onTextChange;
      onSelectionChangeRef.current = onSelectionChange;
    });

    // 处理编辑器变化 - 使用useCallback避免不必要的重渲染
    const handleTextChange = useCallback((delta, oldDelta, source) => {
      if (source === 'user' && quillInstanceRef.current) {
        onTextChangeRef.current?.(delta, oldDelta, source, quillInstanceRef.current);
      }
    }, []);

    const handleSelectionChange = useCallback((range, oldRange, source) => {
      onSelectionChangeRef.current?.(range, oldRange, source);
    }, []);

    useEffect(() => {
      const container = containerRef.current;
      if (!container) return;

      const editorContainer = container.appendChild(
        container.ownerDocument.createElement('div'),
      );
      
      // 自定义工具栏配置
      const toolbarOptions = [
        [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
        [{ 'font': [] }],
        [{ 'size': ['small', false, 'large', 'huge'] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ 'color': [] }, { 'background': [] }],
        [{ 'script': 'sub'}, { 'script': 'super' }],
        [{ 'list': 'ordered'}, { 'list': 'bullet' }],
        [{ 'indent': '-1'}, { 'indent': '+1' }],
        [{ 'direction': 'rtl' }],
        [{ 'align': [] }],
        ['link', 'video', 'image'],
        ['clean']
      ];

      const quill = new Quill(editorContainer, {
        theme: 'snow',
        modules: {
          toolbar: toolbarOptions,
          clipboard: {
            matchVisual: false,
          }
        },
        placeholder: '开始输入内容...',
        readOnly: readOnly || false,
      });

      // 覆盖默认的图片处理程序
      const toolbar = quill.getModule('toolbar');
      toolbar.addHandler('image', () => {
        setIsImageModalOpen(true);
      });

      quillInstanceRef.current = quill;
      initializedRef.current = false;

      // 设置初始内容（如果有）
      if (defaultValueRef.current) {
        // 延迟设置内容，确保编辑器完全初始化
        setTimeout(() => {
          quill.root.innerHTML = defaultValueRef.current;
          initializedRef.current = true;
        }, 100);
      } else {
        initializedRef.current = true;
      }

      // 绑定事件
      quill.on(Quill.events.TEXT_CHANGE, handleTextChange);
      quill.on(Quill.events.SELECTION_CHANGE, handleSelectionChange);

      return () => {
        if (quill) {
          quill.off(Quill.events.TEXT_CHANGE, handleTextChange);
          quill.off(Quill.events.SELECTION_CHANGE, handleSelectionChange);
        }
        container.innerHTML = '';
        quillInstanceRef.current = null;
      };
    }, [handleTextChange, handleSelectionChange, readOnly]);

    useEffect(() => {
      // 添加CSS样式来控制图片大小
      const style = document.createElement('style');
      style.textContent = `
        .ql-editor img {
          max-width: 100% !important;
          height: auto !important;
          max-height: 400px !important;
          object-fit: contain !important;
        }
      `;
      document.head.appendChild(style);
      
      return () => {
        document.head.removeChild(style);
      };
    }, []);

    return (
      <>
        <div ref={containerRef} style={{ minHeight: '100px', transition: 'height 0.2s ease' }}></div>
        <ImageModal 
          isOpen={isImageModalOpen}
          onClose={handleImageModalClose}
          onSelectImage={handleImageSelect}
          osskey={`dingtalk/value/uploadimage/${userid}`}
          onChange={(images) => console.log('图片列表更新:', images)}
        />
      </>
    );
  },
);

Editor.displayName = 'Editor';

export default Editor;